<script>
import SmilingHeartIcon from "@/assets/svg/smiling-heart.svg";
import LibraryBlurb from "@/components/Loanable/LibraryBlurb.vue";
import UserRoleList from "@/components/Loanable/UserRoleList.vue";
import UserBlurb from "@/components/User/UserBlurb.vue";
import { isCoownerOrOwner } from "@/helpers/permissions/loanables";
import { canViewLoanAdminDetails, isBorrower, isLoanAdmin } from "@/helpers/permissions/loans";
import { addTrustedBorrower } from "@/requests/loanableUserRolesRequests";

export default {
  name: "LoanContacts",
  components: { LibraryBlurb, SmilingHeartIcon, UserRoleList, UserBlurb },
  props: {
    loan: {
      type: Object,
      required: true,
    },
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isBorrower() {
      return isBorrower(this.user, this.loan);
    },
    isCoownerOrOwner() {
      return isCoownerOrOwner(this.user, this.loan.loanable);
    },
    isLoanAdmin() {
      return isLoanAdmin(this.user, this.loan);
    },
    canViewLoanAdminDetails() {
      return canViewLoanAdminDetails(this.user, this.loan);
    },
    volunteerUserIds() {
      return this.loan.badges?.volunteer_user_ids ?? [];
    },
    borrowerIsVolunteer() {
      return this.volunteerUserIds.includes(this.loan.borrower_user.id);
    },
  },
  methods: {
    async addBorrowerToTrustedBorrowers() {
      await addTrustedBorrower(this.loan.loanable.id, this.loan.borrower_user.id);
      this.$emit(
        "input",
        {
          ...this.loan,
          is_borrower_trusted: true,
        },
        {
          refreshDashboard: false,
          freshlyLoaded: false,
        }
      );
    },
  },
};
</script>

<template>
  <div class="loan-contacts">
    <div v-if="loan.loanable.library" class="mb-3">
      <h3 class="loan-section-title">{{ $t("loanables.fields.library") | capitalize }}</h3>
      <library-blurb :library="loan.loanable.library" />
    </div>
    <div v-if="isCoownerOrOwner || isLoanAdmin" class="mb-3">
      <h3 class="loan-section-title">{{ $t("loans.fields.borrower_user_id") | capitalize }}</h3>
      <user-blurb
        :show-admin-link="canViewLoanAdminDetails"
        :user="loan.borrower_user"
        show-phone
        show-email
      >
        <template
          v-if="
            loan.loanable.sharing_mode === 'hybrid' &&
            !loan.is_self_service &&
            !loan.is_borrower_trusted
          "
          #dropdown
        >
          <b-dropdown-item variant="success" @click="addBorrowerToTrustedBorrowers">
            <b-icon icon="shield-fill-check" />
            {{ $t("loanables.add_to_trusted_borrowers") }}
          </b-dropdown-item>
        </template>
        <template #nameicon>
          <div
            v-if="borrowerIsVolunteer"
            v-b-tooltip="$t('volunteer')"
            class="loan-contacts__volunteer-icon"
          >
            <smiling-heart-icon class="loan-contacts__volunteer-icon" />
          </div>

          <b-icon
            v-if="loan.is_borrower_trusted"
            v-b-tooltip="$t('loanables.is_trusted_borrower')"
            class="text-primary"
            icon="shield-fill-check"
          />
        </template>
      </user-blurb>
    </div>
    <user-role-list
      v-if="isBorrower || isLoanAdmin || isCoownerOrOwner"
      :user-roles="loan.loanable.merged_user_roles"
      :volunteer-user-ids="volunteerUserIds"
      :show-admin-links="isLoanAdmin"
      :show-contacts-only="!isLoanAdmin && !isCoownerOrOwner"
    >
      <template #title>
        <h3 class="loan-section-title">
          {{ $t("loanables.resource_people") | capitalize }}
        </h3>
      </template>
    </user-role-list>
  </div>
</template>

<i18n>
fr:
  volunteer: Bénévole
en:
  volunteer: Volunteer
</i18n>

<style lang="scss">
.loan-contacts {
  &__volunteer-icon {
    @include volunteer-icon;

    display: inline-block;

    svg {
      margin: 0 auto;
      display: block;
      height: 20px;
      width: 0.875rem;
    }
  }
}
</style>
